import { useApi } from '@shopexperts/shared';
import { useQuery } from '@tanstack/vue-query';
import { computed } from 'vue';
import type { CityFacetValue, FacetsResponse, FacetValue } from '@shopexperts/shared';
import { useCategories } from './useCategories';

export interface SmartFilterOption {
  value: string;
  label: string;
  logo?: string;
}

export interface SmartFilterCityOption extends SmartFilterOption {
  country: string;
}

export interface SmartFilterOptionGroup {
  value: string;
  label: string;
  options: SmartFilterOption[];
}

function toOptions(values: FacetValue[] | undefined): SmartFilterOption[] {
  return (values || []).map(({ value, label, logo }) => ({ value, label, ...(logo ? { logo } : {}) }));
}

/**
 * All selectable expert-listing filter values, straight from the public
 * facets endpoint the website's /experts filters use — so the popup always
 * offers exactly what the listing can filter by.
 */
export function useExpertFacets() {
  const api = useApi();
  const { categoriesWithSubcategories } = useCategories();

  const { data, isLoading, error } = useQuery({
    queryKey: ['public', 'expert-facets'],
    queryFn: async (): Promise<FacetsResponse> => api.public.expert.getFacets({}),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const expertTypeOptions = computed<SmartFilterOption[]>(() => toOptions(data.value?.expert_type));

  // Categories with their subcategories; both levels are valid
  // services_offered slugs. The facets endpoint often returns empty subitems,
  // so subcategories fall back to the categories tree, matched by slug.
  const serviceGroups = computed<SmartFilterOptionGroup[]>(() => {
    const subcategoriesByCategorySlug = new Map(
      categoriesWithSubcategories.value.map(category => [
        category.slug,
        category.subcategories.map(({ slug, name }) => ({ value: slug, label: name })),
      ]),
    );
    return (data.value?.services_offered || []).map(facet => ({
      value: facet.value,
      label: facet.label,
      options: facet.subitems?.length
        ? facet.subitems.map(({ value, label }) => ({ value, label }))
        : subcategoriesByCategorySlug.get(facet.value) || [],
    }));
  });

  const appsAndThemesOptions = computed<SmartFilterOption[]>(() => toOptions(data.value?.apps_and_themes));
  const countryOptions = computed<SmartFilterOption[]>(() => toOptions(data.value?.countries));
  const cityOptions = computed<SmartFilterCityOption[]>(() => (
    (data.value?.cities ?? []).map((city: CityFacetValue) => ({
      value: String(city.value),
      label: city.region ? `${city.label}, ${city.region}` : city.label,
      country: city.country,
    }))
  ));
  const languageOptions = computed<SmartFilterOption[]>(() => toOptions(data.value?.languages));
  const partnerTierOptions = computed<SmartFilterOption[]>(() => toOptions(data.value?.partner_tiers));

  // value -> human label across every facet, for rendering saved selections.
  const labelByFilterValue = computed<Record<string, Record<string, string>>>(() => {
    const maps: Record<string, Record<string, string>> = {
      expert_type: {},
      services_offered: {},
      countries: {},
      cities: {},
      languages: {},
      apps_and_themes: {},
      partner_tiers: {},
    };
    for (const option of expertTypeOptions.value) maps.expert_type[option.value] = option.label;
    for (const group of serviceGroups.value) {
      maps.services_offered[group.value] = group.label;
      for (const option of group.options) maps.services_offered[option.value] = option.label;
    }
    for (const option of countryOptions.value) maps.countries[option.value] = option.label;
    for (const option of cityOptions.value) maps.cities[option.value] = option.label;
    for (const option of languageOptions.value) maps.languages[option.value] = option.label;
    for (const option of appsAndThemesOptions.value) maps.apps_and_themes[option.value] = option.label;
    for (const option of partnerTierOptions.value) maps.partner_tiers[option.value] = option.label;
    return maps;
  });

  return {
    facets: computed(() => data.value),
    isLoading,
    error,
    expertTypeOptions,
    serviceGroups,
    appsAndThemesOptions,
    countryOptions,
    cityOptions,
    languageOptions,
    partnerTierOptions,
    labelByFilterValue,
  };
}

export default useExpertFacets;
